import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMapMarkerAlt, FaFlag, FaCalendar, FaClock } from 'react-icons/fa';
import ShowMap from './ShowMap';
import { Helmet } from 'react-helmet';
import CustomerPage from './CustomerPage';
import { useAuth } from '../../context/AuthContext';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useSelector, useDispatch } from 'react-redux';
import {
    setPickupAddress,
    setDropoffAddress,
    setPickupCoords,
    setDropoffCoords,
    setSuggestions,
    setCurrentField,
    setCurrentLocation,
    setLocationError,
    clearSuggestions,
    setCompletePickupLocation,
    setCompleteDropoffLocation
} from '../../Reducers/locationSlice';
import {
    setSelectedDate,
    setSelectedTime,
} from '../../Reducers/dateTimeSlice';

const HomeDashboard = () => {
    const dispatch = useDispatch();

    const pickupAddress = useSelector(s => s.location.pickupAddress);
    const dropoffAddress = useSelector(s => s.location.dropoffAddress);
    const pickupCoords = useSelector(s => s.location.pickupCoords);
    const dropoffCoords = useSelector(s => s.location.dropoffCoords);
    const suggestions = useSelector(s => s.location.suggestions);
    const currentField = useSelector(s => s.location.currentField);
    const currentLocation = useSelector(s => s.location.currentLocation);
    const locationError = useSelector(s => s.location.locationError);

    const selectedDateRaw = useSelector(s => s.datetime.selectedDate);
    const selectedTime = useSelector(s => s.datetime.selectedTime);

    const selectedDate = useMemo(
        () => new Date(selectedDateRaw ?? Date.now()),
        [selectedDateRaw]
    );

    const [loading, setLoading] = useState(false);
    const { auth } = useAuth();
    const firstName = auth.user?.first_name ?? '';
    const navigate = useNavigate();

    useEffect(() => {
        if (!navigator.geolocation) {
            dispatch(setLocationError('Geolocation is not supported by this browser.'));
            return;
        }
        navigator.geolocation.getCurrentPosition(
            ({ coords: { latitude, longitude } }) =>
                dispatch(setCurrentLocation({ lat: latitude, lng: longitude })),
            err => dispatch(setLocationError('Unable to fetch location. Please enable location services.'))
        );
    }, [dispatch]);

    const fetchSuggestions = async value => {
        if (!window.google?.maps?.places) return;
        const service = new window.google.maps.places.AutocompleteService();

        service.getPlacePredictions(
            {
                input: value,
                types: ['geocode'],
                componentRestrictions: { country: 'za' },
                location: new window.google.maps.LatLng(-28.4796, 24.6987),
                radius: 2000000,
            },
            (predictions, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                    dispatch(setSuggestions(predictions.map(p => p.description)));
                }
            }
        );
    };

    const handleInputChange = (e, field) => {
        const value = e.target.value;
        if (field === 'pickup') {
            dispatch(setPickupAddress(value));
            dispatch(setCurrentField('pickup'));
        } else {
            dispatch(setDropoffAddress(value));
            dispatch(setCurrentField('dropoff'));
        }

        if (value.trim() === '') {
            dispatch(clearSuggestions());
        } else {
            fetchSuggestions(value);
        }
    };

    const handleMapSelect = (coords, address) => {
        if (currentField === 'pickup') {
            dispatch(setCompletePickupLocation(address, coords));
        } else if (currentField === 'dropoff') {
            dispatch(setCompleteDropoffLocation(address, coords));
        }
        dispatch(clearSuggestions());
    };

    const handleSuggestionClick = async (suggestion) => {
        if (!window.google?.maps) return;

        const geocoder = new window.google.maps.Geocoder();
        try {
            const response = await new Promise((resolve, reject) => {
                geocoder.geocode({ address: suggestion }, (results, status) => {
                    if (status === 'OK') resolve(results);
                    else reject(status);
                });
            });

            if (response && response[0]) {
                const location = response[0].geometry.location;
                const coords = { lat: location.lat(), lng: location.lng() };

                if (currentField === 'pickup') {
                    dispatch(setCompletePickupLocation(suggestion, coords));
                } else {
                    dispatch(setCompleteDropoffLocation(suggestion, coords));
                }
                dispatch(clearSuggestions());
            }
        } catch (error) {
            console.error("Geocoding error:", error);
        }
    };

    const handleContinue = async () => {
        if (!pickupAddress || !dropoffAddress || !selectedDate || !selectedTime) {
            alert('Please complete Pickup, Dropoff, Date and Time.');
            return;
        }
        setLoading(true);
        await new Promise(r => setTimeout(r, 1000));
        navigate('/package', {
            state: {
                pickupLocation: pickupAddress,
                dropoffLocation: dropoffAddress,
                pickupCoords,
                dropoffCoords,
                selectedDate,
                selectedTime
            },
        });
        setLoading(false);
    };

    const generateTimeIntervals = date => {
        const out = [];
        const now = new Date();
        const sameDay = date.toDateString() === now.toDateString();

        const start = new Date(date);
        if (sameDay) {
            start.setHours(now.getHours(), now.getMinutes() + 30, 0, 0);
            start.setMinutes(Math.ceil(start.getMinutes() / 15) * 15);
        } else {
            start.setHours(8, 0, 0, 0);
        }

        const end = new Date(date);
        end.setHours(20, 0, 0, 0);

        while (start < end) {
            out.push(
                `${start.getHours().toString().padStart(2, '0')}:${start
                    .getMinutes()
                    .toString()
                    .padStart(2, '0')}`
            );
            start.setMinutes(start.getMinutes() + 30);
        }
        return out;
    };

    const timeIntervals = useMemo(() => generateTimeIntervals(selectedDate), [selectedDate]);

    useEffect(() => {
        if (timeIntervals.length) dispatch(setSelectedTime(timeIntervals[0]));
    }, [timeIntervals, dispatch]);

    const datePickerRef = useRef(null);

    useEffect(() => {
        const obs = new MutationObserver(() => {
            const pop = document.querySelector('.react-datepicker-popper');
            if (!pop) return;
            pop.style.zIndex = 9999;
            pop.style.marginLeft = '40px';
            pop.style.marginTop = '10px';
        });
        obs.observe(document.body, { childList: true, subtree: true });
        return () => obs.disconnect();
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            // Don't clear if clicking on suggestions or location inputs
            if (!e.target.closest('.location-input') && !e.target.closest('.suggestions-container')) {
                dispatch(clearSuggestions());
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dispatch]);

    return (
        <CustomerPage>
            <Helmet>
                <title>Schedule a Delivery | Storage Sprint</title>
            </Helmet>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        Welcome to Storage Sprint,&nbsp;
                        <span className="text-yellow-500">{firstName}</span>!
                    </h1>
                    <p className="text-lg text-gray-600">
                        Your reliable partner for hassle‑free moves and deliveries.
                    </p>
                </div>

                {locationError && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded text-red-700">
                        {locationError}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* date */}
                    <div className="bg-white rounded-xl shadow-md p-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                            <FaCalendar className="h-5 w-5 text-yellow-500 mr-2" />
                            Select Date
                        </label>
                        <ReactDatePicker
                            selected={selectedDate}
                            onChange={d => dispatch(setSelectedDate(d))}
                            dateFormat="dd MMMM yyyy"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                            ref={datePickerRef}
                            minDate={
                                new Date().getHours() >= 20
                                    ? new Date(Date.now() + 24 * 60 * 60 * 1000)
                                    : new Date()
                            }
                            maxDate={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)}
                        />
                    </div>

                    <div className="bg-white rounded-xl shadow-md p-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                            <FaClock className="h-5 w-5 text-yellow-500 mr-2" />
                            Select Time
                        </label>
                        <select
                            value={selectedTime}
                            onChange={e => dispatch(setSelectedTime(e.target.value))}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 appearance-none"
                        >
                            {timeIntervals.map(t => (
                                <option key={t}>{t}</option>
                            ))}
                        </select>
                    </div>

                    <div className="bg-white rounded-xl shadow-md p-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                            <FaMapMarkerAlt className="h-5 w-5 text-yellow-500 mr-2" />
                            Pickup Location
                        </label>
                        <input
                            type="text"
                            placeholder="Enter Pickup Location"
                            value={pickupAddress}
                            onChange={e => handleInputChange(e, 'pickup')}
                            onClick={() => dispatch(setCurrentField('pickup'))}
                            className="location-input w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                        />
                    </div>

                    <div className="bg-white rounded-xl shadow-md p-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                            <FaFlag className="h-5 w-5 text-yellow-500 mr-2" />
                            Drop‑off Location
                        </label>
                        <input
                            type="text"
                            placeholder="Enter Dropoff Location"
                            value={dropoffAddress}
                            onChange={e => handleInputChange(e, 'dropoff')}
                            onClick={() => dispatch(setCurrentField('dropoff'))}
                            className="location-input w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                        />
                    </div>
                </div>

                {suggestions.length > 0 && (pickupAddress || dropoffAddress) && currentField && (
                    <div className="suggestions-container bg-white rounded-lg shadow-lg overflow-hidden mb-6">
                        <ul className="divide-y divide-gray-200">
                            {suggestions.map(s => (
                                <li
                                    key={s}
                                    onClick={() => handleSuggestionClick(s)}
                                    className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center"
                                >
                                    <FaMapMarkerAlt className="h-4 w-4 text-yellow-500 mr-2" />
                                    {s}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="h-96 w-full rounded-xl overflow-hidden shadow-lg mb-6">
                    <ShowMap
                        pickupLocation={pickupCoords}
                        dropoffLocation={dropoffCoords}
                        center={currentLocation}
                        onLocationSelect={handleMapSelect}
                    />
                </div>

                <div className="text-center">
                    <button
                        onClick={handleContinue}
                        disabled={loading}
                        className={`px-8 py-3 rounded-xl font-medium text-white bg-yellow-500 hover:bg-yellow-600 focus:ring-2 focus:ring-yellow-500 transition-colors ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                        {loading ? 'Processing…' : 'Continue to Package'}
                    </button>
                </div>
            </div>
        </CustomerPage>
    );
};

export default HomeDashboard;